import { useEffect, useRef } from "react";
import { useBlocker } from "react-router-dom";
import { useModals } from "src/components/Modals";
import { useToasts } from "src/components/Toasts";
import { useTranslation } from "src/i18n";
import type { ButtonColor, ButtonVariant } from "src/elements/Button";

export type UnsavedChangesSave = () => boolean | Promise<boolean>;

export interface UnsavedChangesSaveAction {
  id: string;
  label: string;
  onSave: UnsavedChangesSave;
  color?: ButtonColor;
  variant?: ButtonVariant;
}

interface Props {
  onSave: UnsavedChangesSave;
  saveActions?: UnsavedChangesSaveAction[];
}

export const useUnsavedChangesGuard = (isDirty: boolean, { onSave, saveActions }: Props) => {
  const { openConfirmModal } = useModals();
  const { toast } = useToasts();
  const { t } = useTranslation("common");
  const isSavingRef = useRef(false);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname,
  );
  const blockerProceedRef = useRef<() => void>(() => undefined);
  const blockerResetRef = useRef<() => void>(() => undefined);
  const onSaveRef = useRef(onSave);
  const saveActionsRef = useRef(saveActions);
  const toastRef = useRef(toast);

  if (blocker.state === "blocked") {
    blockerProceedRef.current = blocker.proceed;
    blockerResetRef.current = blocker.reset;
  }
  onSaveRef.current = onSave;
  saveActionsRef.current = saveActions;
  toastRef.current = toast;

  useEffect(() => {
    if (blocker.state !== "blocked") return;

    const actions = saveActionsRef.current?.length
      ? saveActionsRef.current
      : [
          {
            id: "save",
            label: t("unsavedChanges.save"),
            onSave: () => onSaveRef.current(),
          },
        ];

    const runSave = async (action: UnsavedChangesSaveAction) => {
      if (isSavingRef.current) return false;

      isSavingRef.current = true;
      try {
        const didSave = await action.onSave();
        if (didSave) {
          blockerProceedRef.current();
        }
        return didSave;
      } catch (error) {
        toastRef.current({
          title: t("unsavedChanges.saveFailed"),
          error,
          type: "ERROR",
        });
        return false;
      } finally {
        isSavingRef.current = false;
      }
    };

    openConfirmModal({
      title: t("unsavedChanges.title"),
      description: t("unsavedChanges.description"),
      buttons: [
        {
          id: "keep-editing",
          label: t("unsavedChanges.keepEditing"),
          color: "primary",
          variant: "inverted",
          onClick: () => blockerResetRef.current(),
        },
        {
          id: "leave-anyway",
          label: t("unsavedChanges.leaveAnyway"),
          color: "red",
          onClick: () => blockerProceedRef.current(),
        },
        ...actions.map((action) => ({
          id: action.id,
          label: action.label,
          color: action.color ?? "primary",
          variant: action.variant,
          onClick: () => runSave(action),
        })),
      ],
    });
  }, [blocker.state, openConfirmModal, t]);

  useEffect(() => {
    if (!isDirty) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);
};
